import { useRouter } from "next/router";
import { ReactElement, useState } from "react";
import AccountSignupForm, {
  SignupUserData,
} from "components/Core/Auth/Pages/Signup/AccountSignupForm";
import BrandSignupForm from "components/Core/Auth/Pages/Signup/BrandSignupForm";
import SignupPlanStep from "components/Core/Auth/Pages/Signup/SignupPlanStep";
import SignupStepIndicator from "components/Core/Auth/Pages/Signup/SignupStepIndicator";
import LoginLayout from "components/Layout/LoginLayout";
import PageLoadingSpinner from "components/UI/Loading/PageLoadingSpinner";
import HeadTitle from "components/Layout/PageHead/HeadTitle";
import { useAuth } from "lib/auth/auth";
import {
  useCurrentBrandId,
  useFetchCurrentBrand,
  useSetCurrentBrandId,
} from "lib/auth/currentUserHooks";
import { assertPresence } from "utils/assertions";
import { BRAND_INDEX } from "lib/routing/links";
import { useRouteParam } from "lib/routing/routeParamHooks";

enum SignupStep {
  Account = "Account",
  Brand = "Brand",
  Plan = "Plan",
}

const SIGNUP_STEPS = [SignupStep.Account, SignupStep.Brand, SignupStep.Plan];

export default function SignupPage() {
  const router = useRouter();
  const { handleLogin, isAuthReady, isAuthenticated } = useAuth();
  const setCurrentBrandId = useSetCurrentBrandId();

  const [sessionIdFromUrl] = useRouteParam("session_id");
  const [selectedPlanSlug] = useRouteParam("plan");

  const [isCompletingSignup, setIsCompletingSignup] = useState(false);

  const requestedCurrentBrandId = useCurrentBrandId();
  const {
    data: currentBrandData,
    error: currentBrandError,
    loading: isCurrentBrandLoading,
  } = useFetchCurrentBrand();
  const currentBrand = currentBrandData?.brand;

  const step = (() => {
    if (!isAuthReady) return null;
    if (!isAuthenticated) return SignupStep.Account;
    if (!requestedCurrentBrandId) return SignupStep.Brand;
    if (isCurrentBrandLoading) return null;
    if (currentBrand === null) return SignupStep.Brand;
    if (currentBrand) return SignupStep.Plan;
    return null;
  })();

  const isLoading =
    !isAuthReady ||
    isCompletingSignup ||
    (isAuthenticated &&
      !!requestedCurrentBrandId &&
      (isCurrentBrandLoading || (!currentBrandData && !currentBrandError)));

  const currentStepIndex = step ? SIGNUP_STEPS.indexOf(step) : 0;

  const handleAccountCompleted = async (signupData: SignupUserData) => {
    const auth = signupData.signupUser?.auth;

    assertPresence(auth, "User signup completed without returning auth data.");

    await handleLogin(auth, { redirectPath: null });
  };

  const handleBrandCompleted = async (brandId: string) => {
    setIsCompletingSignup(true);
    await setCurrentBrandId(brandId);
    setIsCompletingSignup(false);
  };

  const handleSignupCompleted = async () => {
    setIsCompletingSignup(true);
    await router.replace(BRAND_INDEX);
  };

  const content = (() => {
    if (isLoading) {
      return <PageLoadingSpinner minHeight="12rem" />;
    }

    if (step === SignupStep.Account) {
      return <AccountSignupForm onCompleted={handleAccountCompleted} />;
    }

    if (step === SignupStep.Brand) {
      return <BrandSignupForm onCompleted={handleBrandCompleted} />;
    }

    if (step === SignupStep.Plan) {
      return (
        <SignupPlanStep
          onContinue={handleSignupCompleted}
          selectedPlanSlug={selectedPlanSlug}
          sessionId={sessionIdFromUrl}
        />
      );
    }

    return null;
  })();

  return (
    <>
      <HeadTitle title="Sign Up" />

      {!isLoading && step && (
        <SignupStepIndicator currentStepIndex={currentStepIndex} />
      )}

      {content}
    </>
  );
}

SignupPage.getLayout = (page: ReactElement) => (
  <LoginLayout
    heading="Create Your Account"
    subheading="Set up your workspace and choose a plan."
  >
    {page}
  </LoginLayout>
);
